import { Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Typography } from "@mui/material";
import { FEATURE, PAGE } from "components/common/CONSTANTS";
import { LabelWithControl } from "components/common/LabelControl";
import { DataGrid } from "components/DataGrid/DataGrid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { columnDefs } from "./doNotOrderListColumnDef";
import { useDispatch, useSelector } from "react-redux";
import { searchMaxCapAction, searchMaxCapReset } from "redux/actions/searchMaxCapAction";
import { addMaxCapAction, addMaxCapReset } from "redux/actions/addMaxCapAction";
import { bulkUpdateMaxCapAction, bulkUpdateMaxCapReset } from "redux/actions/bulkUpdateMaxCapAction";
import { showErrorMessageByCode } from "services/commonServices";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { encryptString } from "components/common/EncryptDecrypt";

export const DoNotOrderList = () => {
    const dispatch = useDispatch();
    const userType = useSelector((state) => state.user);
    const { isSearchLoading, isSearchSuccess, isSearchFail, searchMaxCapResponse } =
        useSelector((state) => state.searchMaxCap);
    const addDNOItemsReducer = useSelector((state) => state.addMaxCap);
    const removeDNOItemsReducer = useSelector((state) => state.bulkUpdateMaxCap);
    const LIMIT = 100;
    const [pins, setPins] = useState();
    const [error, setError] = useState();
    const [searchResultData, setSearchResultData] = useState([]);
    const [apiErrorMessage, setApiErrorMessage] = useState("");
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successDialogMessage, setSuccessDialogMessage] = useState("");
    const [showSpinner, setShowSpinner] = useState(false);
    const [failedItems, setFailedItems] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isAllDataFetched, setAllDataFetched] = useState(false);
    const nextOffsetRef = useRef(0);
    const isFetchingRef = useRef(false);

    const defaultColDef = useMemo(() => {
        return {
            flex: 1,
            sortable: false,
            filter: false,
            resizable: false,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            headerClass: "header-wrap",
            autoSizeStrategy: { type: "fitCellContents" },
        };
    }, []);

    const callSearchApi = useCallback((offset, pins) => {
        if(offset === 0) {
            setSearchResultData([]);
            setAllDataFetched(pins && pins.length > 0);
            nextOffsetRef.current = 0;
        }

        let payload = {};
        if(pins && pins.length > 0) {
            payload = {
                feature: FEATURE.DNO_FEATURE_SETTING,
                status: "active",
                'itemNumber[in]': pins,
            };
        } else {
            payload = {
                feature: FEATURE.DNO_FEATURE_SETTING,
                limit: LIMIT,
                offset: offset,
                status: 'active',
            };
        }
        nextOffsetRef.current = offset + LIMIT;
        isFetchingRef.current = true;
        dispatch(searchMaxCapAction(payload));
    }, [dispatch]);

    useEffect(() => {
        callSearchApi(0);
    }, [callSearchApi]);

    useEffect(() => {
        if (isSearchLoading) {
            setShowSpinner(true);
        } else if (isSearchSuccess) {
            setShowSpinner(false);
            const data = (searchMaxCapResponse?.data?.itemConfigurations || []).map(item => {
                return {
                    item: item.packId,
                    itemDescription: item.itemDescription || "",
                    lastUpdatedBy: item.updatedBy || "",
                    lastUpdatedAt: item.updatedAt || "",
                    status: item.status,
                    selected: false
                }
            });
            setSearchResultData([...searchResultData, ...data]);
            isFetchingRef.current = false;
            dispatch(searchMaxCapReset());
        } else if (isSearchFail) {
            isFetchingRef.current = false;
            dispatch(searchMaxCapReset());
            setShowSpinner(false);
            if (searchMaxCapResponse?.response?.status === 404) {
                if (searchResultData.length <= 0) {
                    setApiErrorMessage("No data found");
                } else {
                    setAllDataFetched(true);
                }
            } else {
                setApiErrorMessage("");
                const { errContent } = showErrorMessageByCode(
                    searchMaxCapResponse
                );
                setApiErrorMessage(errContent);
            }
        }
    }, [isSearchLoading, isSearchSuccess, isSearchFail, searchMaxCapResponse, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (addDNOItemsReducer.isAddLoading) {
            setShowSpinner(true);
        } else if (addDNOItemsReducer.isAddSuccess) {
            setShowSpinner(false);
            const responseData = addDNOItemsReducer.addMaxCapResponse?.data;
            setFailedItems(responseData?.failedItems || []);
            const successCount = responseData?.successItems?.length || 0;
            if (successCount > 0) {
                setSuccessDialogMessage(`${successCount} Item(s) added successfully`);
                setShowSuccessDialog(true);
            }
            dispatch(addMaxCapReset());
        } else if (addDNOItemsReducer.isAddFail) {
            dispatch(addMaxCapReset());
            setShowSpinner(false);
            const { errTitle, errContent } = showErrorMessageByCode(
                addDNOItemsReducer.addMaxCapResponse
            );
            dispatch(showNotificationDialog(errTitle, errContent, 'Ok'));
        }
    }, [addDNOItemsReducer, callSearchApi, dispatch]);

    useEffect(() => {
        if (removeDNOItemsReducer.isBulkUpdateLoading) {
            setShowSpinner(true);
        } else if (removeDNOItemsReducer.isBulkUpdateSuccess) {
            setShowSpinner(false);
            setSuccessDialogMessage(`${removeDNOItemsReducer.bulkUpdateMaxCapResponse?.data?.successItems?.length || 0} Item(s) removed successfully`);
            setShowSuccessDialog(true);
            dispatch(bulkUpdateMaxCapReset());
        } else if (removeDNOItemsReducer.isBulkUpdateFail) {
            dispatch(bulkUpdateMaxCapReset());
            setShowSpinner(false);
            const { errTitle, errContent } = showErrorMessageByCode(
                removeDNOItemsReducer.bulkUpdateMaxCapResponse
            );
            dispatch(showNotificationDialog(errTitle, errContent, 'Ok'));
        }
    }, [removeDNOItemsReducer, callSearchApi, dispatch]); //eslint-disable-line react-hooks/exhaustive-deps

    const resetErrors = () => {
        setError(null);
        setApiErrorMessage(null);
        setFailedItems([]);
    }

    const handlePINChange = (value) => {
        resetErrors();
        if (value.trim()) {
            setPins(value.trim());
        } else {
            if (pins && pins.length > 0) {
                callSearchApi(0);
            }
            setPins("");
        }
    }

    const getValidPins = () => {
        resetErrors();
        if (pins) {
          const enteredPins = pins
            .split(",")
            .filter((pin) => pin.trim() !== "" && !isNaN(pin.trim()))
            .map((pin) => parseInt(pin.trim()));
          const uniquePins = Array.from(new Set(enteredPins));
          if (uniquePins && uniquePins.length > 0) {
            if (uniquePins.length > 100) {
              setError("Maximum allowed entries for pins is 100");
              return [];
            } else {
              return uniquePins;
            }
          }
        }
        setError("Please enter valid pins");
        return [];
      };

    const handleAdd = async () => {
        const validPins = getValidPins();
        if (validPins.length > 0) {
            const encryptedEmailId = await encryptString(userType.loginResponse?.email);
            const itemConfigurations = validPins.map((pin) => ({
                packId: pin,
                status: "active",
            }));
            const payload = {
                feature: FEATURE.DNO_FEATURE_SETTING,
                updatedBy: encryptedEmailId,
                itemConfigurations,
            };
            dispatch(addMaxCapAction(payload));
        }
    };

    const handleSearch = async () => {
        const validPins = getValidPins();
        if (validPins.length > 0) {
            callSearchApi(0, validPins.join(','));
        }
    }

    const handleRemoveButton = async () => {
        if (selectedRows.length > 100) {
            dispatch(showNotificationDialog('Error', "Maximum 100 items can be removed at a time", 'Ok'));
        } else {
            const encryptedEmailId = await encryptString(userType.loginResponse?.email);
            const itemConfigurations = selectedRows.map((item) => ({
                packId: item.item,
                status: "inactive",
            }));
            const payload = {
                feature: FEATURE.DNO_FEATURE_SETTING,
                updatedBy: encryptedEmailId,
                itemConfigurations,
            };
            dispatch(bulkUpdateMaxCapAction(payload));
        }
    };

    const handleSuccessDialogOkPress = () => {
        setSelectedRows([]);
        setSuccessDialogMessage("");
        setPins("");
        setSearchResultData([]);
        setShowSuccessDialog(false);
        callSearchApi(0);
    }

    const getRowStyle = params => {
        if (params.data.status === "inactive") {
            return { backgroundColor: '#f0f0f0', color: '#999' };
        }
        return null;
    }

    const onCellValueChanged = (event) => {
        if (event.colDef.field === "selected") {
            const row = event.data;
            if (row.selected) {
                setSelectedRows([...selectedRows, row]);
            } else {
                const filteredList = selectedRows.filter(
                    (item) => item.item !== row.item
                );
                setSelectedRows(filteredList);
            }
        }
    };

    const onBodyScroll = (params) => {
        var bottomPx = params.api.getVerticalPixelRange().bottom;
        var gridHeight =
            params.api.getDisplayedRowCount() *
            params.api.getSizesForCurrentTheme().rowHeight;
        if (!isAllDataFetched && !isFetchingRef.current && bottomPx >= gridHeight - 100) {
            if (searchResultData.length % LIMIT === 0) {
                callSearchApi(nextOffsetRef.current);
            }
        }
    };

    return (
        <>
            <div className="mainPageStyling">
                {showSpinner && (
                    <Backdrop
                        sx={{
                            color: "#fff",
                            flexDirection: "column",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                        open={true}
                    >
                        <CircularProgress color="inherit" />

                        <Typography variant="h6" sx={{ marginTop: 2 }}>
                            Loading...
                        </Typography>
                    </Backdrop>
                )}
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Typography component="h1" variant="h5" sx={{ mb: 2, mr: 2 }}>
                        {PAGE.DO_NOT_ORDER_LIST.label}
                    </Typography>
                </div>
                <div className="labelControl">
                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1.5rem",
                            marginBottom: "10px",
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={9} md={11}>
                                <LabelWithControl label="PIN's" labelId="pins">
                                    <textarea
                                        id='pins'
                                        name='name'
                                        rows={'7'}
                                        cols={'100'}
                                        placeholder="Enter comma seperated PINs (e.g., PIN1,PIN2,PIN3...)"
                                        style={{ resize: "none" }}
                                        value={pins}
                                        onChange={(e) => handlePINChange(e.target.value.replace(/[^0-9,]/g, ''))}
                                    />
                                </LabelWithControl>
                                {error && <div style={{ color: 'red', marginTop: '5px' }}>{error}</div>}
                            </Grid>
                            <Grid item xs={12} sm={3} md={1}>
                                <Grid container spacing={1} direction="column">
                                    <Grid style={{ marginTop: '10px' }}>
                                        <LabelWithControl
                                            labelVisible="hidden"
                                            label="ADD"
                                            labelId="addButton"
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{
                                                    width: "-webkit-fill-available",
                                                    height: "25px",
                                                    fontSize: "12px",
                                                }}
                                                onClick={handleAdd}
                                                disabled={!pins}
                                                fullWidth
                                            >
                                                ADD
                                            </Button>
                                        </LabelWithControl>
                                    </Grid>
                                    <Grid>
                                        <LabelWithControl
                                            labelVisible="hidden"
                                            label="Search"
                                            labelId="searchButton"
                                        >
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{
                                                    width: "-webkit-fill-available",
                                                    height: "25px",
                                                    fontSize: "12px",
                                                }}
                                                onClick={handleSearch}
                                                disabled={!pins}
                                                fullWidth
                                            >
                                                SEARCH
                                            </Button>
                                        </LabelWithControl>
                                    </Grid>
                                    {(failedItems.length > 0 && <Grid >
                                        <LabelWithControl
                                            labelVisible="hidden"
                                            label="Error"
                                            labelId="errorLink"
                                        >
                                            <Link style={{ textAlign: 'center' }} onClick={() => setShowErrorDialog(true)}>Error PINs</Link>
                                        </LabelWithControl>
                                    </Grid>)}
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>
                    {
                        searchResultData.length > 0 ? (
                            <div style={{ width: "100%", height: "calc( 100vh - 340px)" }}>
                                <DataGrid
                                    columnDefs={columnDefs}
                                    rowData={searchResultData}
                                    defaultColDef={defaultColDef}
                                    getRowStyle={getRowStyle}
                                    onCellValueChanged={onCellValueChanged}
                                    onBodyScroll={onBodyScroll}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    color: "red",
                                    marginBottom: "10px",
                                }}
                            >
                                {apiErrorMessage}
                            </div>
                        )
                    }
                </div>
            </div>

            {searchResultData.length > 0 && (
                <>
                    <Grid container spacing={2}>
                        {/* Right side buttons */}


                        <Grid item xs={6} container justifyContent="flex-start" spacing={2}>
                            <Grid item>
                                <div className="parentPoButtons">
                                    <Button
                                        className="commonPOButtons"
                                        variant="contained"
                                        color="primary"
                                        disabled={selectedRows.length <= 0}
                                        onClick={handleRemoveButton}
                                    >
                                        REMOVE
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>

                    </Grid>
                </>
            )}

            <Dialog open={showSuccessDialog}>
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {successDialogMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSuccessDialogOkPress} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showErrorDialog}>
                <DialogTitle>Invalid PIN's</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <div className='totalOrdersCreated totalOrdersCreatedAlign'>Failed Item(s): {failedItems.join(',')}</div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setShowErrorDialog(false) }} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}